import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Building2, Users, Mail, Plus, LogOut, ShieldCheck, ArrowRight, Check, Settings, Receipt, MessageSquare, Gauge } from "lucide-react";import SEO from "../components/SEO";
import { useProAuth } from "../components/ProAuthContext";
import VerifyEmailBanner from "../components/VerifyEmailBanner";
import ImTopbarButton from "../im/ImTopbarButton";
import { API } from "../apiBase";
export default function MssanteEspace() {
  const { user, logout, refresh } = useProAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const showWelcome = params.get("welcome") === "1";
  const paymentStatus = params.get("payment");
  const paymentOrderId = params.get("order");
  const [mailboxes, setMailboxes] = useState([]);
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [mbRes, stRes] = await Promise.all([
          axios.get(`${API}/pro/mailboxes`),
          axios.get(`${API}/pro/structures`),
        ]);
        if (alive) {
          setMailboxes(mbRes.data || []);
          setStructures(stRes.data || []);
        }
      } catch (e) {
        console.error("load espace:", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/mssante", { replace: true });
  };

  const totalUsers = structures.reduce((sum, s) => sum + (s.user_count || 0), 0);

  return (
    <div className="bg-[var(--mc-navy-950)] min-h-screen" data-testid="page-mssante-espace">
      <SEO title="Mon espace sécurisé · MetaConnect" canonical="/mssante/espace" noIndex />

      <section className="hex-grid pt-32 pb-10">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[var(--mc-mint)] mb-2">
                <ShieldCheck size={13} /> Espace sécurisé
              </div>
              <h1 className="font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold leading-tight">
                Bonjour, {user?.first_name || ""}
              </h1>
              <p className="mt-2 text-[13.5px] text-[var(--mc-muted)]">
                {user?.profession}
                {user?.id_type && user?.professional_id && ` · ${user.id_type} ${user.professional_id}`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <ImTopbarButton />
              <button onClick={async () => {
                try {
                  const { data } = await axios.post(`${API}/pro/billing/portal`);
                  if (data?.url) window.location.href = data.url;
                } catch (err) {
                  alert(err?.response?.data?.detail || "Le portail de facturation est indisponible.");
                }
              }} data-testid="esp-billing-portal"
                      className="btn-ghost px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5">
                <Receipt size={13} /> Mes factures
              </button>
              <Link to="/mssante/espace/securite" data-testid="esp-securite"
                    className="btn-ghost px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5">
                <ShieldCheck size={13} /> Sécurité {user?.twofa_enabled && <span className="text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full border border-[rgba(110,231,197,0.4)] text-[var(--mc-mint)]">2FA</span>}
              </Link>
              <button onClick={handleLogout} data-testid="esp-logout"
                      className="btn-ghost px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5">
                <LogOut size={13} /> Se déconnecter
              </button>
            </div>
          </div>

          {showWelcome && (
            <div className="mt-6 rounded-xl border border-[rgba(110,231,197,0.3)] bg-[rgba(110,231,197,0.06)] p-5 flex items-start gap-3">
              <div className="grid place-items-center h-9 w-9 rounded-full bg-[rgba(110,231,197,0.18)] text-[var(--mc-mint)] shrink-0">
                <Check size={18} />
              </div>
              <div>
                <div className="text-[#0F2B5B] font-medium text-[14px]">Votre espace sécurisé est créé</div>
                <p className="text-[12.5px] text-[var(--mc-muted)] mt-0.5">
                  Un email de bienvenue a été envoyé à {user?.email}. Vous pouvez créer votre première messagerie MSSanté ci-dessous.
                </p>
              </div>
            </div>
          )}

          {paymentStatus === "success" && (
            <div className="mt-6 rounded-xl border border-[rgba(110,231,197,0.45)] bg-[rgba(110,231,197,0.10)] p-5 flex items-start gap-3" data-testid="payment-success-banner">
              <div className="grid place-items-center h-10 w-10 rounded-full bg-[rgba(110,231,197,0.22)] text-[var(--mc-mint)] shrink-0">
                <Check size={20} />
              </div>
              <div className="flex-1">
                <div className="text-[#0F2B5B] font-semibold text-[15px]">Paiement validé — votre BAL est en cours d'activation</div>
                <p className="text-[13px] text-[var(--mc-muted)] mt-1 leading-relaxed">
                  Le mandat SEPA a été enregistré. Le prélèvement bancaire est lancé : votre boîte
                  MSSanté sera <strong className="text-[#0F2B5B]">activée sous 3 à 7 jours ouvrés</strong>{" "}
                  après confirmation du prélèvement par votre banque. Vous recevrez un email
                  automatique de confirmation, accompagné du contrat signé en pièce jointe, dès que
                  c'est fait.
                </p>
                {paymentOrderId && (
                  <a href={`${API}/pro/mssante-orders/${paymentOrderId}/contract.pdf`}
                     target="_blank" rel="noreferrer"
                     data-testid="payment-success-contract-pdf"
                     className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] text-[#0284C7] underline underline-offset-2 hover:text-[#0F2B5B]">
                    Télécharger mon contrat MSSanté signé (PDF) <ArrowRight size={12} />
                  </a>
                )}
              </div>
            </div>
          )}

          {paymentStatus === "cancelled" && (
            <div className="mt-6 rounded-xl border border-[rgba(255,170,80,0.40)] bg-[rgba(255,170,80,0.06)] p-5 flex items-start gap-3" data-testid="payment-cancelled-banner">
              <div className="grid place-items-center h-9 w-9 rounded-full bg-[rgba(255,170,80,0.18)] text-[#B45309] shrink-0">
                <ArrowRight size={18} className="rotate-180" />
              </div>
              <div>
                <div className="text-[#0F2B5B] font-medium text-[14px]">Paiement non finalisé</div>
                <p className="text-[12.5px] text-[var(--mc-muted)] mt-0.5">
                  Votre commande n'a pas été activée. Vous pouvez la reprendre quand vous le
                  souhaitez depuis le bouton « Mes factures » ou en relançant la création.
                </p>
              </div>
            </div>
          )}

          {user && user.email_verified === false && (
            <div className="mt-6">
              <VerifyEmailBanner email={user.email} onVerified={refresh} />
            </div>
          )}
        </div>
      </section>

      <section className="py-8 lg:py-10">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/mssante/espace/structures" data-testid="esp-stat-structures"
                  className="mc-card p-5 group hover:border-[var(--mc-blue-400)] transition">
              <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(110,231,197,0.12)] border border-[rgba(110,231,197,0.3)] text-[var(--mc-mint)] mb-3">
                <Building2 size={18} />
              </div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--mc-muted)]">Mes structures</div>
              <div className="mt-1 font-display text-[#0F2B5B] text-3xl">{structures.length}</div>
              <div className="mt-2 text-[11px] text-[#0284C7] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Gérer <ArrowRight size={11} />
              </div>
            </Link>
            <StatCard testid="esp-stat-users" Icon={Users} label="Mes utilisateurs" value={totalUsers} />
            <StatCard testid="esp-stat-mailboxes" Icon={Mail} label="Mes messageries" value={mailboxes.length} />
            <Link to="/mssante/espace/messagerie/nouvelle" data-testid="esp-cta-new"
                  className="mc-card p-5 group hover:border-[var(--mc-blue-400)] transition flex items-center gap-3">
              <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(45,123,255,0.18)] border border-[var(--mc-blue-400)] text-[#0284C7]">
                <Plus size={18} />
              </div>
              <div className="flex-1">
                <div className="text-[#0F2B5B] font-medium text-[14px]">Nouvelle messagerie</div>
                <div className="text-[11px] text-[var(--mc-muted)]">Créer une boîte MSSanté</div>
              </div>
              <ArrowRight size={14} className="text-[#0284C7] group-hover:translate-x-0.5 transition" />
            </Link>
          </div>

          {/* Quick access to instant messaging (inter-PS, non-HDS) */}
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <Link
              to="/mssante/espace/telesoin"
              data-testid="esp-cta-telesoin"
              className="mc-card p-5 group hover:border-[var(--mc-blue-400)] transition flex items-center gap-3"
            >
              <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(45,123,255,0.14)] border border-[rgba(127,179,255,0.32)] text-[#0284C7]">
                <Gauge size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[#0F2B5B] font-medium text-[14px] inline-flex items-center gap-2">
                  Quota télésoin &amp; contrats
                  <span className="text-[9.5px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full bg-[#0EA5E9] text-white font-semibold">Nouveau</span>
                </div>
                <div className="text-[11px] text-[var(--mc-muted)] mt-0.5">
                  Plafond 20 % du CA, 4 actes/an/patient, planning annuel et contrats officiels (Ordre, salariés, confrères)
                </div>
              </div>
              <ArrowRight size={14} className="text-[#0284C7] group-hover:translate-x-0.5 transition" />
            </Link>
            <Link
              to="/mssante/espace/facturation"
              data-testid="esp-cta-facturation"
              className="mc-card p-5 group hover:border-[var(--mc-blue-400)] transition flex items-center gap-3"
            >
              <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(45,123,255,0.14)] border border-[rgba(127,179,255,0.32)] text-[#0284C7]">
                <Receipt size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[#0F2B5B] font-medium text-[14px] inline-flex items-center gap-2">
                  Facturation électronique &amp; comptable
                  <span className="text-[9.5px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full bg-[#0EA5E9] text-white font-semibold">Nouveau</span>
                </div>
                <div className="text-[11px] text-[var(--mc-muted)] mt-0.5">
                  Factur-X (EN 16931), TVA exonérée/hors nomenclature, e-reporting et mandat expert-comptable
                </div>
              </div>
              <ArrowRight size={14} className="text-[#0284C7] group-hover:translate-x-0.5 transition" />
            </Link>
            <Link
              to="/mssante/espace/messagerie-instantanee"
              data-testid="esp-cta-instant-messaging"
              className="mc-card p-5 group hover:border-[var(--mc-mint)] transition flex items-center gap-3"
            >
              <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(110,231,197,0.14)] border border-[rgba(110,231,197,0.32)] text-[var(--mc-mint)]">
                <MessageSquare size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[#0F2B5B] font-medium text-[14px] inline-flex items-center gap-2">
                  Messagerie instantanée
                  <span className="text-[9.5px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full bg-[var(--mc-mint)] text-[#0F2B5B] font-semibold">Nouveau</span>
                </div>
                <div className="text-[11px] text-[var(--mc-muted)] mt-0.5">
                  Discuter en temps réel avec un confrère — Inter-PS uniquement (pas de pièce jointe, non HDS)
                </div>
              </div>
              <ArrowRight size={14} className="text-[var(--mc-mint)] group-hover:translate-x-0.5 transition" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-8 lg:py-12">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#0284C7] mb-5">
            Mes messageries MSSanté
          </div>

          {loading && <div className="text-[var(--mc-muted)] text-[13px]">Chargement…</div>}

          {!loading && mailboxes.length === 0 && (
            <div className="mc-card p-10 text-center">
              <Mail size={28} className="mx-auto text-[#0284C7]" />
              <h3 className="mt-4 font-display text-[#0F2B5B] text-lg">Aucune messagerie pour le moment</h3>
              <p className="mt-2 text-[13px] text-[var(--mc-muted)] max-w-md mx-auto">
                Créez votre première boîte MSSanté en quelques étapes guidées.
              </p>
              <Link to="/mssante/espace/messagerie/nouvelle"
                    className="mt-5 btn-primary px-6 py-2.5 rounded-full text-[13px] font-medium inline-flex items-center gap-2">
                <Plus size={14} /> Créer ma première messagerie
              </Link>
            </div>
          )}

          {!loading && mailboxes.length > 0 && (
            <div className="grid gap-3">
              {mailboxes.map((m) => {
                const accessCount = ((m.cotitulaire_user_ids || []).length) + ((m.delegataire_user_ids || []).length);
                return (
                  <Link key={m.id} to={`/mssante/espace/messagerie/${m.id}/acces`}
                        data-testid={`esp-mailbox-${m.id}`}
                        className="mc-card p-5 flex items-center gap-4 flex-wrap hover:border-[var(--mc-blue-400)] transition">
                    <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(110,231,197,0.12)] border border-[rgba(110,231,197,0.3)] text-[var(--mc-mint)] shrink-0">
                      <Mail size={18} />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-[#0F2B5B] font-medium text-[14px] break-all">{m.mailbox_full_email}</div>
                      <div className="text-[12px] text-[var(--mc-muted)] mt-0.5">
                        {m.structure_name} · Responsable : {m.user_first_name} {m.user_last_name}
                        {accessCount > 0 && <> · {accessCount} utilisateur(s) associé(s)</>}
                      </div>
                    </div>
                    <span className={`text-[11px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full border ${
                      m.status === "active" ? "border-[rgba(110,231,197,0.4)] text-[var(--mc-mint)] bg-[rgba(110,231,197,0.06)]"
                      : m.status === "cancelled" ? "border-[rgba(255,120,120,0.4)] text-[#FFB4B4] bg-[rgba(255,120,120,0.06)]"
                      : "border-[rgba(127,179,255,0.3)] text-[#0284C7] bg-[rgba(45,123,255,0.05)]"
                    }`}>
                      {m.status === "active" ? "Active" : m.status === "cancelled" ? "Résiliée" : "En traitement"}
                    </span>
                    <Settings size={14} className="text-[var(--mc-muted)]" />
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function StatCard({ Icon, label, value, testid }) {
  return (
    <div className="mc-card p-5" data-testid={testid}>
      <div className="grid place-items-center h-10 w-10 rounded-lg bg-[rgba(110,231,197,0.12)] border border-[rgba(110,231,197,0.3)] text-[var(--mc-mint)] mb-3">
        <Icon size={18} />
      </div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--mc-muted)]">{label}</div>
      <div className="mt-1 font-display text-[#0F2B5B] text-3xl">{value}</div>
    </div>
  );
}
